// src/pages/TrackCreation/Step1.tsx
import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { uploadFileToS3Complete } from "../../../../../api/s3";
import { useTrackCreation } from "../../../../../context/TrackCreationContext";
import { useTranslation } from "react-i18next";
import { RouteParamsWithLanguage } from "../../../../../types/screensTypes";
import Button from "../../../../../components/ui/Button";
import ImageList from "../../../../../components/images/ImageList";
import { saveQuiz, getQuizById } from "../../../../../api";
import { FileInput } from "../../../../../components/common/FileInput";
import TitleSubtitle from "../../../../../components/common/TitleSubtitle";
import ImageSpecsBadges from "../../../../../components/common/ImageSpecsBadges";
import { useImageValidation } from "../../../../../hooks/useImageValidation";
import { IMAGE_FORMAT, IMAGE_MAX_SIZE_200_KB } from "../../../../../utils/image";
import stepStyles from "../../createStep.module.css";

const QuizPicture: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { quizId, language } = useParams<RouteParamsWithLanguage>();
    const { trackData } = useTrackCreation();

    const [pictureUrl, setPictureUrl] = useState<string | null>(null);
    const imageValidation = useImageValidation({
        type: "quiz",
        imageUrl: pictureUrl || undefined,
    });

    const uploadTitle = t("uploadImage");
    const uploadSubtitle = t("pleaseInsertImageForVisitorsToSee");

    const handleFileSelected = async (files: File | File[]) => {
        const file = Array.isArray(files) ? files[0] : files;

        if (file) {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("No authentication token found");
                return;
            }

            await imageValidation.validateImage(file);

            uploadFileToS3Complete(
                {
                    file,
                    isPublic: true,
                    isFolder: false,
                },
                token,
            )
                .then((result: string) => {
                    setPictureUrl(result);
                })
                .catch((error) => {
                    console.log(error);
                });
        }
    };

    const saveQuizHandler = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        if (!token || !trackId || !quizId || !language) {
            return;
        }
        if (!pictureUrl || pictureUrl.trim() === "") {
            return;
        }
        await saveQuiz({
            token,
            trackId,
            quizId,
            picture: pictureUrl,
        });
        navigate(location.pathname.replace(/\/quiz-[^/]+\/.+$/, "/quiz-content"));
    };

    const getQuizInfo = async (trackId: string, quizId: string, lang: string): Promise<void> => {
        const token = localStorage.getItem("token");
        if (!token) {
            return;
        }
        const quizInfo = await getQuizById({
            token,
            trackId,
            quizId,
            language: lang,
        });

        const quizPicture = quizInfo?.pictureUrl || null;
        if (quizPicture) {
            setPictureUrl(quizPicture);
        }
    };

    const setPicturesFromList = (pictures: string[]): void => {
        setPictureUrl(pictures.length > 0 ? pictures[0] : null);
        if (pictures.length === 0) {
            imageValidation.clearValidation();
        }
    };

    useEffect(() => {
        if (quizId && quizId !== "" && quizId !== "null" && trackData.trackId && language) {
            getQuizInfo(trackData.trackId, quizId, language);
        }
    }, [quizId, trackData.trackId, language]);

    return (
        <div className={`${stepStyles.editScreen} ${stepStyles.quizEditScreen}`}>
            <div className={stepStyles.stepDescription}>
                <TitleSubtitle title={uploadTitle} subtitle={uploadSubtitle} layout="edit" />
                <ImageSpecsBadges spec="quiz" warnings={imageValidation.warnings} />
            </div>
            <div className={stepStyles.stepDescription} style={{ marginTop: "30px" }}>
                <div className={stepStyles.fileUpload}>
                    <FileInput
                        onFileSelected={handleFileSelected}
                        showButton={true}
                        acceptedFileTypes={IMAGE_FORMAT}
                        maxSize={IMAGE_MAX_SIZE_200_KB}
                        maxSizeLabel={t("imageMaxSize200kb")}
                    />
                </div>
            </div>
            <div className="photo-gallery">
                <ImageList
                    pictures={pictureUrl ? [pictureUrl] : []}
                    setPictures={(pics: string[]) => setPicturesFromList(pics)}
                />
            </div>
            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                    label={t("continue")}
                    onClick={saveQuizHandler}
                    disabled={!pictureUrl || pictureUrl.trim() === ""}
                />
            </div>
        </div>
    );
};

export default QuizPicture;
